import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

export interface ToastMessage {
  id: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
}

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private toastSubject = new Subject<ToastMessage>();
  public toast$ = this.toastSubject.asObservable();
  private activeToasts: ToastMessage[] = [];

  private generateId(): string {
    // NOSONAR - Math.random() is safe here: used only for temporary UI toast IDs,
    // not for security-sensitive operations like tokens or cryptographic purposes
    return Math.random().toString(36).substr(2, 9);
  }

  private showToast(message: string, type: 'success' | 'error' | 'warning' | 'info', duration: number) {
    // Check if same message already exists
    const existingToast = this.activeToasts.find((toast) => toast.message === message && toast.type === type);

    if (existingToast) {
      return; // Don't show duplicate
    }

    const toast: ToastMessage = {
      id: this.generateId(),
      message,
      type,
      duration,
    };

    this.activeToasts.push(toast);
    this.toastSubject.next(toast);

    // Remove from active toasts after duration
    setTimeout(() => {
      this.activeToasts = this.activeToasts.filter((t) => t.id !== toast.id);
    }, duration);
  }

  showSuccess(message: string, duration: number = 5000) {
    this.showToast(message, 'success', duration);
  }

  showError(message: string, duration: number = 8000) {
    this.showToast(message, 'error', duration);
  }

  showWarning(message: string, duration: number = 6000) {
    this.showToast(message, 'warning', duration);
  }

  showInfo(message: string, duration: number = 5000) {
    this.showToast(message, 'info', duration);
  }

  // Method to remove toast (called when user clicks close)
  removeToast(toastId: string) {
    this.activeToasts = this.activeToasts.filter((t) => t.id !== toastId);
  }
}
